import { kpiHelp, kpiLabel } from './analyticsKpiDefinitions.js';
import { buildMerchantKpiDetails } from './analyticsKpiDetails.js';
import { mapCanauxMetierUiLabels } from '../../utils/canalMetierLabels.js';

const fmtEuro = (n) => Number(n || 0).toFixed(2).replace('.', ',');

const dashNum = (n) => (n == null || n === '' || Number.isNaN(Number(n)) ? '—' : String(n));

function seriesFromRows(rows, getValue, label, color) {
  const points = (rows || [])
    .map((row) => ({
      date: row?.date ?? row?.createdAt ?? row?.label ?? null,
      value: Number(getValue(row) ?? 0),
    }))
    .filter((p) => p.date != null && p.date !== '');
  if (!points.length) return null;
  return { label, points, color };
}

function canalPanierRows(canaux) {
  return (canaux || [])
    .filter((c) => Number(c.commandes || 0) > 0)
    .map((c) => ({
      label: c.label || c.key || c.canal,
      commandes: c.commandes,
      ca: c.ca,
      panier:
        Number(c.commandes) > 0
          ? Math.round((Number(c.ca) / Number(c.commandes)) * 100) / 100
          : 0,
    }));
}

const PAGE_TYPE_LABELS = {
  MERCHANT: 'Pages boutique (vues)',
  PRODUCT: 'Fiches produit (vues)',
  CHECKOUT: 'Pages panier / paiement (vues)',
  OTHER: 'Autres pages (vues)',
};

/**
 * Construit le modèle d’affichage d’une page détail Analytics
 * à partir des données overview (commerçant) ou dashboard (SA).
 */
export function buildAnalyticsDetailPageModel({
  detailKey,
  mode, // 'merchant' | 'sa'
  overview,
  saAnalytics,
  periodLabel,
}) {
  if (mode === 'merchant' && overview) {
    return buildMerchantDetail(detailKey, overview, periodLabel);
  }
  return buildSaDetail(detailKey, saAnalytics, periodLabel);
}

function buildMerchantDetail(detailKey, data, periodLabel) {
  const kpi = data?.kpi || {};
  const gift = data?.gift || {};
  const trafic = data?.trafic || {};
  const comportement = data?.comportement || {};
  const base = buildMerchantKpiDetails({
    kpi,
    gift,
    trafic,
    commandesParStatut: data?.commandesParStatut || [],
    fmtEuro,
  });
  const period =
    periodLabel ||
    (data?.periode?.debutLocal && data?.periode?.finLocal
      ? `${data.periode.debutLocal} → ${data.periode.finLocal}`
      : data?.periode?.preset || 'période');

  const common = { periodLabel: period, actionSoon: base[detailKey]?.comingSoon };

  switch (detailKey) {
    case 'ca':
      return {
        ...common,
        title: kpiLabel('caNetVentes'),
        definition: kpiHelp('caNetVentes'),
        mainValue: `${fmtEuro(kpi.ca?.value)} €`,
        deltaLabel:
          kpi.ca?.deltaPct != null ? `${kpi.ca.deltaPct}% vs période précédente` : null,
        understand: {
          sectionTitle: 'Ce qui est pris en compte',
          summary: base.ca.summary,
          method: base.ca.method,
          rows: base.ca.rows,
        },
        evolutionSeries: seriesFromRows(data?.evolution, (r) => r.ca, 'CA net', '#059669'),
        detail: {
          title: 'Décomposition',
          list: [
            {
              label: 'Chiffre d’affaires encaissé avant remboursements',
              value: `${fmtEuro(kpi.ca?.brut)} €`,
            },
            { label: 'Remboursements', value: `${fmtEuro(kpi.ca?.rembourse)} €` },
            {
              label: 'Chiffre d’affaires après remboursements',
              value: `${fmtEuro(kpi.ca?.value)} €`,
            },
          ],
          canaux: data?.canaux || null,
        },
      };
    case 'commandes': {
      const volume = Number(kpi.volume?.value ?? 0);
      const canaux = mapCanauxMetierUiLabels(data?.canaux || []);
      const channelOrders = canaux.reduce((s, c) => s + Number(c.commandes || 0), 0);
      const canalPie = canaux
        .filter((c) => Number(c.commandes || 0) > 0)
        .map((c) => {
          const count = Number(c.commandes || 0);
          const denom = channelOrders > 0 ? channelOrders : volume;
          return {
            key: c.key || c.label,
            label: c.label || c.key,
            count,
            pct: denom > 0 ? Math.round((count / denom) * 1000) / 10 : 0,
          };
        });
      return {
        ...common,
        title: kpiLabel('commandesPayees'),
        definition: kpiHelp('commandesPayees'),
        mainValue: String(volume),
        deltaLabel:
          kpi.volume?.deltaPct != null
            ? `${kpi.volume.deltaPct > 0 ? '+' : ''}${kpi.volume.deltaPct} % par rapport à la période précédente`
            : null,
        understand: {
          sectionTitle: 'Ce qui est pris en compte',
          summary: base.commandes.summary,
          rows: [
            {
              label: 'Commandes payées',
              value: String(volume),
              hint: kpiHelp('commandesPayees'),
            },
            {
              label: 'CA net associé',
              value: `${fmtEuro(kpi.ca?.value)} €`,
              hint: kpiHelp('caNetVentes'),
            },
            {
              label: 'Panier moyen',
              value: `${fmtEuro(kpi.panierMoyen?.value)} €`,
              hint: kpiHelp('panierMoyen'),
            },
            {
              label: 'Variation',
              value:
                kpi.volume?.deltaPct != null
                  ? `${kpi.volume.deltaPct > 0 ? '+' : ''}${kpi.volume.deltaPct} %`
                  : '—',
              hint:
                'Comparaison avec la période précédente de même durée (juste avant la période affichée).',
            },
          ],
        },
        evolutionSeries: seriesFromRows(
          data?.evolution,
          (r) => r.commandes ?? r.volume,
          'Commandes',
          '#2563eb'
        ),
        detail: {
          title: 'Répartition des commandes par canal',
          // Camembert = canaux réconciliés (même source que le tableau Performance par canal).
          canalPie,
          canaux,
          list: canalPie.map((r) => ({
            label: r.label,
            value: `${r.count} · ${r.pct} %`,
          })),
        },
      };
    }
    case 'panier':
      return {
        ...common,
        title: kpiLabel('panierMoyen'),
        definition: kpiHelp('panierMoyen'),
        mainValue: `${fmtEuro(kpi.panierMoyen?.value)} €`,
        deltaLabel:
          kpi.panierMoyen?.deltaPct != null
            ? `${kpi.panierMoyen.deltaPct > 0 ? '+' : ''}${kpi.panierMoyen.deltaPct} % par rapport à la période précédente`
            : null,
        understand: {
          sectionTitle: 'Ce qui est pris en compte',
          summary: base.panier.summary,
          method: base.panier.method,
          rows: [
            {
              label: 'Panier moyen obtenu',
              value: `${fmtEuro(kpi.panierMoyen?.value)} €`,
              hint: kpiHelp('panierMoyen'),
            },
          ],
        },
        evolutionSeries: seriesFromRows(
          data?.evolution,
          (r) => {
            const cmd = Number(r.commandes ?? r.volume ?? 0);
            const ca = Number(r.ca ?? 0);
            return cmd > 0 ? Math.round((ca / cmd) * 100) / 100 : 0;
          },
          'Panier moyen',
          '#7c3aed'
        ),
        detail: {
          title: 'Panier moyen par canal',
          canauxPanier: canalPanierRows(data?.canaux),
        },
      };
    case 'remboursements': {
      const rembourseCount =
        (data?.commandesParStatut || []).find((s) => s.statut === 'REMBOURSE')?.count ?? null;
      const brut = Number(kpi.ca?.brut || 0);
      const remb = Number(kpi.ca?.rembourse || 0);
      const taux = brut > 0 ? Math.round((remb / brut) * 1000) / 10 : null;
      return {
        ...common,
        title: kpiLabel('remboursements'),
        definition: kpiHelp('remboursements'),
        mainValue: `${fmtEuro(remb)} €`,
        hidePerformanceBanner: true,
        understand: {
          sectionTitle: 'Ce qui est pris en compte',
          summary: base.remboursements.summary,
          method: base.remboursements.method,
          rows: [
            {
              label: 'Montant remboursé',
              value: `${fmtEuro(remb)} €`,
              hint: kpiHelp('remboursements'),
            },
            {
              label: 'Commandes remboursées',
              value: rembourseCount != null ? String(rembourseCount) : '—',
              hint: 'Commandes au statut remboursé dans le périmètre des commandes payées.',
            },
            {
              label: 'Remboursement moyen',
              value:
                rembourseCount > 0
                  ? `${fmtEuro(remb / rembourseCount)} €`
                  : remb > 0
                    ? '—'
                    : '0,00 €',
            },
            {
              label: 'Taux de remboursement',
              value: taux != null ? `${taux} %` : '—',
              hint: 'Montant remboursé ÷ chiffre d’affaires avant remboursements (sur la période).',
            },
          ],
        },
        detail: {
          title: 'Lecture',
          list: [
            {
              label: 'Avant remboursements',
              value: `${fmtEuro(brut)} €`,
            },
            { label: 'Remboursements', value: `${fmtEuro(remb)} €` },
            {
              label: 'Après remboursements',
              value: `${fmtEuro(kpi.ca?.value)} €`,
            },
          ],
        },
      };
    }
    case 'trafic': {
      const geo = (trafic.parPays || []).filter(
        (g) => (g.pays || g.country) && !/inconnu|unknown|null/i.test(String(g.pays || g.country))
      );
      const knownGeoVues = geo.reduce(
        (s, g) => s + Number(g.pagesVues ?? g.visites ?? g.vues ?? 0),
        0
      );
      const consulted = comportement.topProduitsConsultes || [];
      const catMap = {};
      for (const p of consulted) {
        const cat = p.categorie || 'Sans catégorie';
        catMap[cat] = (catMap[cat] || 0) + Number(p.vues || 0);
      }
      const categories = Object.entries(catMap)
        .map(([label, vues]) => ({ label, vues }))
        .sort((a, b) => b.vues - a.vues);
      const hasRealCategory = consulted.some((p) => p.categorie);
      return {
        ...common,
        title: 'Trafic site boutique',
        definition: kpiHelp('pagesVues'),
        mainValue: String(trafic.totalVues ?? 0),
        mainSuffix: ' pages vues',
        understand: {
          sectionTitle: 'Ce qui est pris en compte',
          summary: 'Pages consultées et visites sur votre boutique en ligne pendant la période.',
          rows: [
            {
              label: 'Pages vues',
              value: String(trafic.totalVues ?? 0),
              hint: kpiHelp('pagesVues'),
            },
            {
              label: 'Visites',
              value: String(trafic.sessionsUniques ?? 0),
              hint: kpiHelp('sessions'),
            },
            {
              label: 'Pages vues par visite',
              value: String(trafic.vuesParVisite ?? 0),
              hint: kpiHelp('pagesParSession'),
            },
            {
              label: 'Taux de transformation en achat',
              value:
                trafic.conversionVisiteCommande != null
                  ? `${trafic.conversionVisiteCommande}%`
                  : '—',
              hint: kpiHelp('conversion'),
            },
          ],
        },
        evolutionSeries: seriesFromRows(trafic.evolution, (r) => r.vues, 'Pages vues', '#0284c7'),
        detail: {
          title: 'Analyse trafic',
          evolutionDays: trafic.evolution || [],
          heuresPics: trafic.heuresPics || [],
          hideGeo: knownGeoVues === 0,
          geo: knownGeoVues > 0 ? geo : [],
          categoriesConsulted: hasRealCategory ? categories.filter((c) => c.label !== 'Sans catégorie') : [],
          topProduitsConsultes: consulted,
          list: (trafic.parTypePage || []).map((t) => ({
            label: PAGE_TYPE_LABELS[t.pageType] || `${t.pageType} (vues)`,
            value: String(t.vues),
          })),
        },
      };
    }
    case 'gift':
      return {
        ...common,
        title: 'Cartes cadeaux',
        definition: kpiHelp('giftVendu'),
        mainValue: String(gift.soldCount ?? 0),
        mainSuffix: ' cartes vendues',
        hidePerformanceBanner: true,
        understand: {
          sectionTitle: 'Ce qui est pris en compte',
          summary: base.gift.summary,
          rows: [
            {
              label: 'Cartes vendues',
              value: String(gift.soldCount ?? 0),
              hint: `${fmtEuro(gift.soldAmount ?? gift.soldNetAmount)} € de valeur vendue sur la période.`,
            },
            {
              label: 'Valeur vendue',
              value: `${fmtEuro(gift.soldAmount ?? gift.soldNetAmount)} €`,
              hint: kpiHelp('giftVendu'),
            },
            {
              label: 'Montant utilisé pendant la période',
              value: `${fmtEuro(gift.usedAmount)} €`,
              hint:
                'Toutes cartes confondues (y compris vendues avant la période). ' +
                kpiHelp('giftUtilise'),
            },
            {
              label: 'Cartes actives / encours',
              value: `${gift.activeCount ?? 0} · ${fmtEuro(gift.outstandingAmount)} €`,
              hint: kpiHelp('giftEncours'),
            },
            {
              label: 'Taux d’utilisation des cartes vendues sur la période',
              value:
                gift.utilizationRate != null
                  ? `${Math.round(gift.utilizationRate * 100)} %`
                  : '—',
              hint: kpiHelp('giftTaux'),
            },
          ],
        },
        detail: {
          title: 'Situation et expirations',
          list: [
            {
              label: 'Remboursé / annulé (période)',
              value: `${fmtEuro(gift.refundedAmount)} €`,
            },
            {
              label: 'Expiration sous 30 jours',
              value: `${gift?.expiring30d?.count ?? 0} carte(s) · ${fmtEuro(gift?.expiring30d?.amount)} €`,
            },
            {
              label: 'Expiration sous 7 jours',
              value: `${gift?.expiring7d?.count ?? 0} carte(s) · ${fmtEuro(gift?.expiring7d?.amount)} €`,
            },
            {
              label: 'Expirées avec solde',
              value: `${gift?.expiredWithBalance?.count ?? 0} · ${fmtEuro(gift?.expiredWithBalance?.amount)} €`,
            },
          ],
          giftCardList: null,
        },
      };
    case 'produits': {
      const topCa = data?.topProduitsCa || [];
      const topQty = data?.topProduitsQuantite || [];
      const totalCaRef = Number(kpi.ca?.value || 0);
      // Préférer topCategoriesCa API ; sinon agréger depuis les produits déjà catégorisés du top.
      let cats = data?.topCategoriesCa || [];
      if (!cats.length) {
        // Agrégation locale depuis les tops produits déjà catégorisés (dédup produitId).
        const seen = new Set();
        const byCatUnique = {};
        for (const p of [...topCa, ...topQty]) {
          const cat = p.categorie;
          if (!cat || seen.has(p.produitId)) continue;
          seen.add(p.produitId);
          if (!byCatUnique[cat]) byCatUnique[cat] = { categorie: cat, quantite: 0, ca: 0 };
          byCatUnique[cat].quantite += Number(p.quantite || 0);
          byCatUnique[cat].ca += Number(p.ca || 0);
        }
        cats = Object.values(byCatUnique)
          .map((c) => ({
            ...c,
            ca: Math.round(Number(c.ca) * 100) / 100,
            partCaPct:
              totalCaRef > 0 ? Math.round((Number(c.ca) / totalCaRef) * 1000) / 10 : 0,
          }))
          .sort((a, b) => b.ca - a.ca);
      }
      const totalQty = topCa.reduce((s, p) => s + Number(p.quantite || 0), 0);
      const refs = new Set([
        ...topCa.map((p) => p.produitId),
        ...topQty.map((p) => p.produitId),
      ]).size;
      const top1 = topCa[0] || null;
      const cat1 = cats[0] || null;
      return {
        ...common,
        title: 'Performance des produits',
        definition:
          'Ventes produits sur les commandes payées de la période (hors émission carte cadeau).',
        mainValue: `${fmtEuro(kpi.ca?.value)} €`,
        mainSuffix: ' de CA net',
        hidePerformanceBanner: true,
        understand: {
          sectionTitle: 'Ce qui est pris en compte',
          summary:
            'CA et quantités des lignes produits des commandes payées. Changez la période (7j / 30j / 90j / 1 an / personnalisé) pour comparer la saisonnalité.',
          rows: [
            {
              label: 'CA produits (CA net ventes)',
              value: `${fmtEuro(kpi.ca?.value)} €`,
              hint: kpiHelp('caNetVentes'),
            },
            {
              label: 'Quantité vendue (top CA)',
              value: String(totalQty),
            },
            {
              label: 'Références dans le top',
              value: String(refs),
            },
            {
              label: 'Produit n°1 (CA)',
              value: top1 ? `${top1.nom} · ${fmtEuro(top1.ca)} €` : '—',
            },
            {
              label: 'Catégorie n°1',
              value: cat1
                ? `${cat1.categorie} · ${fmtEuro(cat1.ca)} €${
                    cat1.partCaPct != null ? ` · ${cat1.partCaPct} %` : ''
                  }`
                : '—',
              hint: 'Catégorie catalogue avec le plus gros CA parmi les produits vendus affichés.',
            },
          ],
        },
        detail: {
          title: 'Ventes et catégories',
          categoriesCa: cats,
          produitsCa: topCa,
          produitsQuantite: topQty,
          periodHint:
            'Utilisez le sélecteur de période pour comparer 7 jours, 30 jours, 90 jours ou 1 an — sans prévision IA.',
        },
      };
    }
    case 'clients':
    case 'comportement':
    case 'utilisateurs': {
      const visiteurs = comportement.visiteurs || {};
      const top = (comportement.topClientsConnectes || []).map((c) => ({
        ...c,
        estClientBoutique:
          c.estClientBoutique === true ||
          Number(c.nbCommandes || 0) > 0 ||
          Boolean(c.carteFidelite),
      }));
      const funnel = comportement.funnel || {};
      return {
        ...common,
        title: 'Comportement des visiteurs',
        definition: kpiHelp('visiteursIdentifies'),
        mainValue: String(visiteurs.connectes ?? 0),
        mainSuffix: ' visiteurs identifiés',
        hidePerformanceBanner: true,
        understand: {
          summary:
            'Visiteur identifié = compte connecté ayant consulté CETTE boutique (page_views.userId). Ce n’est pas forcément un client du module Clients.',
          rows: [
            {
              label: 'Visiteurs identifiés',
              value: String(visiteurs.connectes ?? 0),
              hint: kpiHelp('visiteursIdentifies'),
            },
            {
              label: 'Visiteurs sans compte',
              value: String(visiteurs.anonymes ?? 0),
              hint: kpiHelp('visiteursAnonymes'),
            },
            {
              label: 'Part des visites identifiées',
              value: `${visiteurs.pctConnectes ?? 0}%`,
              hint: 'Part des sessions boutique avec un compte connecté.',
            },
          ],
        },
        detail: {
          title: 'Parcours, recherches et visiteurs',
          funnelEtapes: funnel.etapes || [],
          pointsAbandon: comportement.pointsAbandon || [],
          topRecherches: comportement.topRecherches || [],
          topProduitsConsultes: comportement.topProduitsConsultes || [],
          clients: top,
          identityAvailable: top.length > 0,
          commercantId: data?.commercantId || data?.commercant?.id || null,
        },
      };
    }
    default:
      return null;
  }
}

function buildSaDetail(detailKey, analytics, periodLabel) {
  if (!analytics) return null;
  const period = periodLabel || analytics.periodLabel || 'période';
  const common = { periodLabel: period };

  switch (detailKey) {
    case 'ca':
      return {
        ...common,
        title: kpiLabel('caNetVentes'),
        definition: kpiHelp('caNetVentes'),
        mainValue: `${fmtEuro(analytics.revenus?.total)} €`,
        deltaLabel:
          analytics.revenus?.evolution != null
            ? `${analytics.revenus.evolution}% vs préc.`
            : null,
        understand: {
          formula: 'CA net = CA brut − remboursements',
          rows: [
            { label: 'CA brut', value: `${fmtEuro(analytics.revenus?.brut)} €` },
            {
              label: 'Remboursements',
              value: `${fmtEuro(analytics.revenus?.rembourse)} €`,
              hint: kpiHelp('remboursements'),
            },
            { label: 'CA net', value: `${fmtEuro(analytics.revenus?.total)} €` },
          ],
        },
        evolutionSeries: seriesFromRows(
          analytics.revenus?.parJour,
          (d) => d.montant ?? d.revenus ?? d.value ?? d.ca,
          'CA net',
          '#059669'
        ),
        detail: {
          title: 'Évolution journalière',
          chartDays: analytics.revenus?.parJour || [],
          canaux: analytics.commandes?.parCanal || null,
        },
        actionSoon: 'Relances CA ciblées',
      };
    case 'commandes':
      return {
        ...common,
        title: kpiLabel('commandesPayees'),
        definition: kpiHelp('commandesPayees'),
        mainValue: dashNum(analytics.commandes?.total),
        evolutionSeries: seriesFromRows(
          analytics.commandes?.parJour,
          (d) => d.count ?? d.value,
          'Commandes',
          '#2563eb'
        ),
        understand: {
          summary: 'Volume de commandes payées sur le périmètre sélectionné.',
          rows: [
            { label: 'Commandes', value: dashNum(analytics.commandes?.total) },
            {
              label: 'Variation',
              value:
                analytics.commandes?.evolution != null
                  ? `${analytics.commandes.evolution}%`
                  : '—',
            },
          ],
        },
        detail: {
          title: 'Répartition',
          list: [
            ...(analytics.commandes?.parStatut || []).map((s) => ({
              label: `Statut ${s.statut || s.label}`,
              value: String(s.count ?? s.value ?? 0),
            })),
            ...(analytics.commandes?.parCanal || []).map((s) => ({
              label: `Canal ${s.canal || s.label}`,
              value: String(s.count ?? s.value ?? 0),
            })),
          ],
        },
      };
    case 'panier':
      return {
        ...common,
        title: kpiLabel('panierMoyen'),
        definition: kpiHelp('panierMoyen'),
        mainValue: `${fmtEuro(analytics.revenus?.panierMoyen)} €`,
        evolutionSeries: seriesFromRows(
          analytics.revenus?.parJour,
          (d) => {
            const cmd = Number(d.commandes ?? d.count ?? 0);
            const ca = Number(d.montant ?? d.revenus ?? d.value ?? d.ca ?? 0);
            return cmd > 0 ? Math.round((ca / cmd) * 100) / 100 : 0;
          },
          'Panier moyen',
          '#7c3aed'
        ),
        understand: {
          formula: 'Panier moyen = CA net ventes / commandes payées',
          rows: [
            { label: 'CA net', value: `${fmtEuro(analytics.revenus?.total)} €` },
            { label: 'Commandes payées', value: dashNum(analytics.commandes?.total) },
            {
              label: 'Panier moyen',
              value: `${fmtEuro(analytics.revenus?.panierMoyen)} €`,
            },
          ],
        },
        detail: {
          title: 'Panier moyen par canal',
          canauxPanier: canalPanierRows(analytics.commandes?.parCanal),
        },
      };
    case 'remboursements':
      return {
        ...common,
        title: kpiLabel('remboursements'),
        definition: kpiHelp('remboursements'),
        mainValue: `${fmtEuro(analytics.revenus?.rembourse)} €`,
        understand: {
          formula: 'CA net = CA brut − remboursements',
          rows: [
            { label: 'Montant remboursé', value: `${fmtEuro(analytics.revenus?.rembourse)} €` },
            { label: 'CA brut', value: `${fmtEuro(analytics.revenus?.brut)} €` },
            { label: 'CA net', value: `${fmtEuro(analytics.revenus?.total)} €` },
          ],
        },
        actionSoon: 'Liste détaillée des remboursements',
      };
    case 'utilisateurs':
    case 'clients':
      return {
        ...common,
        title: kpiLabel('comptesPlateforme'),
        definition: kpiHelp('comptesPlateforme'),
        mainValue: dashNum(analytics.utilisateurs?.total),
        understand: {
          summary:
            'Snapshot actuel de tous les comptes Clickorico — indépendant de la période sélectionnée. Différent du module Clients (clients boutique filtrés).',
          rows: [
            {
              label: 'Comptes plateforme (total actuel)',
              value: dashNum(analytics.utilisateurs?.total),
              hint: kpiHelp('comptesPlateforme'),
            },
            {
              label: 'Boutiques (total actuel)',
              value: dashNum(analytics.commercants?.total),
              hint: kpiHelp('commercants'),
            },
            {
              label: 'Boutiques actives (total actuel)',
              value: dashNum(analytics.commercants?.actifs),
              hint: kpiHelp('commercantsActifs'),
            },
          ],
        },
        detail: {
          title: 'À ne pas confondre',
          list: [
            {
              label: 'Module Clients',
              value:
                'Liste filtrée (clients actifs, hors admins/commerçants/gérants/invités caisse) — souvent un nombre plus bas.',
            },
          ],
        },
        actionSoon: 'Segmentation visiteurs avancée',
      };
    case 'commercants':
      return {
        ...common,
        title: kpiLabel('commercants'),
        definition: kpiHelp('commercants'),
        mainValue: dashNum(analytics.commercants?.total),
        understand: {
          rows: [
            { label: 'Total boutiques', value: dashNum(analytics.commercants?.total) },
            {
              label: 'Actives',
              value: dashNum(analytics.commercants?.actifs),
            },
          ],
        },
      };
    case 'trafic':
      return {
        ...common,
        title: 'Trafic',
        definition: kpiHelp('pagesVues'),
        mainValue: dashNum(analytics.vues?.total),
        mainSuffix: ' vues',
        evolutionSeries: seriesFromRows(
          analytics.vues?.evolution,
          (d) => d.vues ?? d.vuesCommercants ?? d.total,
          'Vues',
          '#0284c7'
        ),
        understand: {
          rows: [
            {
              label: 'Vues commerçants',
              value: dashNum(analytics.vues?.commercants),
              hint: kpiHelp('vuesCommercants'),
            },
            {
              label: 'Moyenne / boutique active',
              value: dashNum(analytics.vues?.moyenneParCommercant),
              hint: kpiHelp('moyenneBoutiqueActive'),
            },
            {
              label: 'Pages vues (total affiché)',
              value: dashNum(analytics.vues?.total),
              hint: kpiHelp('pagesVues'),
            },
          ],
          summary:
            'Le tracking des pages plateforme (hors boutique commerçant) n’est pas branché — non affiché comme KPI primaire.',
        },
        detail: {
          title: 'Évolution des vues',
          evolutionDays: (analytics.vues?.evolution || []).map((d) => ({
            date: d.date,
            sessions: d.sessions ?? 0,
            vues: d.vues ?? d.vuesCommercants ?? d.total ?? 0,
          })),
        },
      };
    default:
      return null;
  }
}

export { PAGE_TYPE_LABELS };
